import { resolveLabelRasterEditRules } from "@shared/extensions/label-raster/schema"

import { prettyPrintJson, tryParseSchema } from "../lib/utils"

function ColorSwatch({ color }: { color: string }) {
  return (
    <span
      className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm border border-border/70"
      style={{ backgroundColor: color }}
      aria-hidden
    />
  )
}

export function SchemaSummary({ type, schema }: { type: string; schema: string }) {
  const parsed = tryParseSchema(schema)
  if (!parsed) {
    return (
      <pre className="max-h-[360px] max-w-full overflow-auto whitespace-pre-wrap break-words text-xs leading-relaxed">
        {prettyPrintJson(schema)}
      </pre>
    )
  }

  if (type === "mask") {
    const labels = Array.isArray(parsed.labels) ? parsed.labels : []
    return (
      <div className="space-y-1 text-sm">
        <p className="text-xs text-muted-foreground">Labels ({labels.length})</p>
        {labels.length === 0 ? (
          <p className="text-xs text-muted-foreground">No labels configured.</p>
        ) : (
          <ul className="space-y-1">
            {labels.map((label) => {
              if (!label || typeof label !== "object") return null
              const item = label as Record<string, unknown>
              return (
                <li
                  key={`${String(item.key ?? "label")}-${String(item.label ?? item.key ?? "Unnamed")}`}
                  className="rounded border p-2 text-xs"
                >
                  <div className="flex items-start gap-2">
                    <ColorSwatch color={String(item.color ?? "#6b7280")} />
                    <span>
                      <span className="font-medium">{String(item.label ?? item.key ?? "Unnamed")}</span>
                      <span className="text-muted-foreground">
                        {" "}
                        •{" "}
                        {item.instance_policy &&
                        typeof item.instance_policy === "object" &&
                        "mode" in item.instance_policy &&
                        item.instance_policy.mode === "multiple"
                          ? "Multiple allowed"
                          : "One allowed"}
                      </span>
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }

  if (type === "score") {
    const fields = Array.isArray(parsed.fields) ? parsed.fields : []
    return (
      <div className="space-y-1 text-sm">
        <p className="text-xs text-muted-foreground">Fields ({fields.length})</p>
        {fields.length === 0 ? (
          <p className="text-xs text-muted-foreground">No fields configured.</p>
        ) : (
          <ul className="space-y-1">
            {fields.map((field) => {
              if (!field || typeof field !== "object") return null
              const item = field as Record<string, unknown>
              return (
                <li
                  key={`${String(item.key ?? "field")}-${String(item.label ?? item.key ?? "Unnamed")}`}
                  className="rounded border p-2 text-xs"
                >
                  <span className="font-medium">{String(item.label ?? item.key ?? "Unnamed")}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    ({String(item.type ?? "unknown")}) • {item.required === false ? "optional" : "required"}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }

  if (type === "vector") {
    const groups = Array.isArray(parsed.groups) ? parsed.groups : []
    const flatLabels = Array.isArray(parsed.labels) ? parsed.labels : []
    const totalLabels =
      groups.reduce(
        (sum: number, g: Record<string, unknown>) => sum + (Array.isArray(g.labels) ? g.labels.length : 0),
        0
      ) + flatLabels.length

    return (
      <div className="space-y-2 text-sm">
        <p className="text-xs text-muted-foreground">Vector labels ({totalLabels})</p>
        {totalLabels === 0 ? (
          <p className="text-xs text-muted-foreground">No label restrictions configured.</p>
        ) : (
          <div className="space-y-2">
            {groups.map((group: Record<string, unknown>, groupIdx: number) => {
              const groupLabels = Array.isArray(group.labels) ? group.labels : []
              return (
                <div key={`group-${groupIdx}`} className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    {String(group.name ?? group.key ?? "Unnamed group")} ({groupLabels.length})
                  </p>
                  <ul className="space-y-1">
                    {groupLabels.map((label: unknown, labelIdx: number) => {
                      if (!label || typeof label !== "object") return null
                      const item = label as Record<string, unknown>
                      return (
                        <li key={`group-${groupIdx}-label-${labelIdx}`} className="rounded border p-2 text-xs">
                          <div className="flex items-start gap-2">
                            <ColorSwatch color={String(item.color ?? "#6b7280")} />
                            <span className="font-medium">{String(item.label ?? item.key ?? "Unnamed")}</span>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
            {flatLabels.length > 0 && (
              <ul className="space-y-1">
                {flatLabels.map((label: unknown, idx: number) => {
                  if (!label || typeof label !== "object") return null
                  const item = label as Record<string, unknown>
                  return (
                    <li key={`flat-label-${idx}`} className="rounded border p-2 text-xs">
                      <div className="flex items-start gap-2">
                        <ColorSwatch color={String(item.color ?? "#6b7280")} />
                        <span className="font-medium">{String(item.label ?? item.key ?? "Unnamed")}</span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    )
  }

  if (type === "image_label") {
    const options = Array.isArray(parsed.options) ? parsed.options : []
    return (
      <div className="space-y-1 text-sm">
        <p className="text-xs text-muted-foreground">
          Options ({options.length}) • {parsed.multi_select ? "Multiple choices" : "Single choice"}
          {Boolean(parsed.series_mapping) && " • Series mapping"}
        </p>
        {options.length === 0 ? (
          <p className="text-xs text-muted-foreground">No options configured.</p>
        ) : (
          <ul className="space-y-1">
            {options.map((option) => {
              if (!option || typeof option !== "object") return null
              const item = option as Record<string, unknown>
              return (
                <li
                  key={`${String(item.value ?? "option")}-${String(item.label ?? item.value ?? "Unnamed")}`}
                  className="rounded border p-2 text-xs"
                >
                  <span className="font-medium">{String(item.label ?? item.value ?? "Unnamed")}</span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }

  if (type === "text") {
    return (
      <div className="space-y-1 text-xs">
        <p>
          <span className="font-medium">Required:</span> {parsed.required ? "Yes" : "No"}
        </p>
        <p>
          <span className="font-medium">Applies to:</span>{" "}
          {parsed.scope === "series" ? "Single image series" : "Whole case"}
        </p>
        <p>
          <span className="font-medium">Max Length:</span> {parsed.max_length ? String(parsed.max_length) : "Not set"}
        </p>
        <p>
          <span className="font-medium">Placeholder:</span>{" "}
          {parsed.placeholder ? String(parsed.placeholder) : "Not set"}
        </p>
      </div>
    )
  }

  if (type === "label_raster") {
    const lr = parsed as {
      domain?: string
      editable_class_ids?: number[]
      unannotated_id?: number
      brush_overwritable_ids?: number[]
      default_brush_radius_px?: number
      require_edit?: boolean
    }
    const rules = resolveLabelRasterEditRules(lr)
    const describeClassId = (id: number) =>
      id === 0 ? "background" : id === rules.unannotatedId ? `unannotated (${id})` : String(id)
    return (
      <div className="space-y-1 text-xs">
        <p>
          <span className="font-medium">Edits overlay domain:</span> {lr.domain ?? "Not set"}
        </p>
        <p>
          <span className="font-medium">Editable classes:</span>{" "}
          {lr.editable_class_ids?.length ? lr.editable_class_ids.join(", ") : "All classes in the mask palette"}
        </p>
        <p>
          <span className="font-medium">Eraser writes:</span> {describeClassId(rules.unannotatedId)}
        </p>
        <p>
          <span className="font-medium">Brush may overwrite:</span>{" "}
          {rules.overwritableIds.map(describeClassId).join(", ")}
        </p>
        <p>
          <span className="font-medium">Default brush radius:</span> {lr.default_brush_radius_px ?? 24} px
        </p>
        <p>
          <span className="font-medium">Requires an edit to complete:</span> {lr.require_edit ? "Yes" : "No"}
        </p>
      </div>
    )
  }

  return (
    <pre className="max-h-[360px] max-w-full overflow-auto whitespace-pre-wrap break-words text-xs leading-relaxed">
      {prettyPrintJson(schema)}
    </pre>
  )
}
